import { FlexDirection, Justify } from "yoga-layout";
import type { EventHandlerCallback } from "../engines/coreComponents/types";
import { DragType } from "../engines/interactionEngine/interactionObserverConstants";
import type { NodeStyle } from "../engines/layoutEngine/layout.types";
import type { FSC, SCNode } from "../engines/renderingEngine/fsc.types";
import {
  BoxNode,
  IconNode,
  TextNode
} from "../engines/renderingEngine/primitiveNodes";
import {
  HORIZONTAL_DROP_INDICATOR_HEIGHT,
  HorizontalDropIndicator
} from "./HorizontalDropIndicator";
import { PALETTE, THEME } from "./colorPalette.constants";
import { styles } from "./scomponents.styles";
import { getStepTextMaxLength } from "./scomponents.utils";

type ComponentMappingKeys =
  | "defaultTitlePrefixIcon"
  | "upStreamRuleIndicatorIcon"
  | "downStreamRuleIndicatorIcon";

type StepNodeProps = {
  title: string;
  children: SCNode<unknown>[];
  selected: boolean;
  key: string;
  expanded: boolean;

  addChildNode?: SCNode<unknown>;

  onCollapseToggle: EventHandlerCallback;
  onDropped: EventHandlerCallback;
  onDragStart: EventHandlerCallback;
  onClick: EventHandlerCallback;
  onDropping?: EventHandlerCallback;
  onDroppingOut?: EventHandlerCallback;

  swimlaneId: string; // Remove when DS-4766 is resolved
  theme: THEME;
  styleOverride: Partial<NodeStyle>;
  readonly: boolean;
  upStreamRulesCount: number;
  downStreamRulesCount: number;
  onUpStreamRuleIndicatorClick: () => void;
  onDownStreamRuleIndicatorClick: () => void;

  isDroppingAbove?: boolean;
  dropIndicatorIndex: number;
  disableExpandAndCollapse?: boolean;

  viewType?: "swimlaneFlow" | "structured" | "workflowFlow";
};

const DROP_TYPES_CONTAINER = [DragType.STEP, DragType.TEMPLATE_STEP];

const DROP_TYPES_HEADER = [DragType.FIELD, DragType.NEW_FIELD];

export const StepNode: FSC<StepNodeProps> = ({
  key,
  children,
  addChildNode,
  expanded = true,
  onCollapseToggle,
  onDropped,
  onDragStart,
  onDropping,
  onDroppingOut,
  selected,
  onClick,
  swimlaneId,
  theme = THEME.LIGHT,
  styleOverride,
  readonly,
  upStreamRulesCount,
  downStreamRulesCount,
  onDownStreamRuleIndicatorClick,
  onUpStreamRuleIndicatorClick,
  title,
  isDroppingAbove,
  dropIndicatorIndex,
  disableExpandAndCollapse,
  viewType = "structured"
}) => {
  const style = styles[theme].step;
  const palette = PALETTE[theme];

  const isFlowMode = viewType === "swimlaneFlow" || viewType === "workflowFlow";
  const isSwimlaneFlow = viewType === "swimlaneFlow";
  /**
   * These are props only applicable when we're NOT in readonly mode.
   */
  const nonReadonlyPropsHeader: Partial<Parameters<typeof BoxNode>[0]> =
    readonly
      ? {}
      : {
          dragType: DragType.STEP,
          dropTypes: DROP_TYPES_HEADER,
          dragHandleFor: key,
          eventHandlers: {
            onDropped: onDropped,
            onDragStart: onDragStart,
            onDropping,
            onDroppingOut
          }
        };

  /**
   * These are props only applicable when we're NOT in readonly mode.
   */
  const nonReadonlyPropsContainer: Partial<Parameters<typeof BoxNode>[0]> =
    readonly
      ? {}
      : {
          // TODO: We have to also add drag type to container mentioned in `dragHandleFor`
          // Because when checking for "dropping", we usually just find the dragType using
          // the node that's in dragContext (at that point we don't know who the "handler" was)
          // Therefore, since we have added this prop to the container as well.
          // This is an intermediate fix until we figure out a better way to do this.
          dragType: DragType.STEP,
          dropTypes: DROP_TYPES_CONTAINER,
          eventHandlers: {
            onDropped: onDropped,
            onDropping,
            onDroppingOut
          }
        };

  const upstreamIndicator = BoxNode({
    key: `${key}-StepUpStreamRuleIndicatorContainer`,
    style: isFlowMode
      ? style.flowRuleIndicatorContainer
      : style.ruleIndicatorContainer,
    eventHandlers: {
      onClick: onUpStreamRuleIndicatorClick
    },
    children: [
      IconNode({
        key: `${key}-StepUpStreamRuleIndicatorIcon`,
        icon: "ArrowUpLight",
        size: 15,
        color: palette.RULE_INDICATOR_UPSTREAM_ICON_COLOR,
        children: []
      }),
      TextNode({
        key: `${key}-StepUpStreamRuleRuleIndicatorText`,
        text: upStreamRulesCount.toString(),
        textStyle: style.ruleIndicatorTitle,
        children: []
      })
    ]
  });

  const downstreamIndicator = BoxNode({
    key: `${key}-StepDownStreamRuleIndicatorContainer`,
    style: isFlowMode
      ? style.flowRuleIndicatorContainer
      : style.ruleIndicatorContainer,
    eventHandlers: {
      onClick: onDownStreamRuleIndicatorClick
    },
    children: [
      IconNode({
        key: `${key}-StepDownStreamRuleIndicatorIcon`,
        icon: "ArrowDownLight",
        size: 15,
        color: palette.RULE_INDICATOR_DOWNSTREAM_ICON_COLOR,
        children: []
      }),
      TextNode({
        key: `${key}-StepDownStreamRuleIndicatorText`,
        text: downStreamRulesCount.toString(),
        textStyle: style.ruleIndicatorTitle,
        children: []
      })
    ]
  });

  const CollapseTriggerNode = BoxNode({
    key: `${key}-CollapseTrigger`,
    style: style.collapseTrigger,
    eventHandlers: {
      onClick: onCollapseToggle
    },
    children: [
      IconNode({
        key: `${key}-CollapseIcon`,
        icon: expanded ? "ChevronDownLight" : "ChevronRightLight",
        size: 14,
        color: palette.STEP_CARET,
        style: styles.light.noStyle,
        children: []
      })
    ]
  });

  const defaultTitlePrefixIcon = BoxNode({
    key: `${key}-Icon-Container`,
    style:
      isFlowMode && !isSwimlaneFlow
        ? style.flowTitleDefaultIconContainer
        : styles.light.noStyle,
    children: [
      IconNode({
        key: `${key}-Icon`,
        icon: "SectionJump",
        size: 16,
        color: palette.STEP_ICON,
        style: styles.light.noStyle,
        children: []
      })
    ]
  });

  const componentMapping: Record<ComponentMappingKeys, SCNode> = {
    defaultTitlePrefixIcon: defaultTitlePrefixIcon,
    upStreamRuleIndicatorIcon: upstreamIndicator,
    downStreamRuleIndicatorIcon: downstreamIndicator
  };

  const headerRightSideChildren = getHeaderRightSideChildren({
    isSwimlaneFlow,
    isFlowMode,
    upStreamRulesCount,
    downStreamRulesCount
  });

  const headerTitleChildren = [];

  const titlePrefixIcon = getTitlePrefixIcon({
    isSwimlaneFlow,
    isFlowMode,
    upStreamRulesCount
  });

  if (titlePrefixIcon) {
    headerTitleChildren.push(componentMapping[titlePrefixIcon]);
  }
  const titleContainerStyle = isFlowMode
    ? titlePrefixIcon
      ? style.flowTitleContainer
      : style.flowTitleContainerOnlyText
    : style.titleContainer;

  let ruleIndicatorCount = 0;
  if (titlePrefixIcon) {
    ruleIndicatorCount++;
  }
  if (headerRightSideChildren.length > 0) {
    ruleIndicatorCount++;
  }
  const stepTextStyles = {
    ...style.titleNormal,
    ...getStepTextMaxLength(ruleIndicatorCount)
  };

  headerTitleChildren.push(
    TextNode({
      text: title || "Untitled step",
      key: `${key}-Title`,
      textStyle: title ? stepTextStyles : style.titleItalic,
      style: titleContainerStyle,
      children: []
    })
  );

  const headerComponent = BoxNode({
    key: `${key}-Header`,
    style: isFlowMode ? style.flowHeaderContainer : style.headerContainer,
    children: [
      BoxNode({
        key: `${key}-HeaderLeft`,
        style: isFlowMode ? style.flowHeaderLeft : style.headerLeft,
        children: headerTitleChildren
      }),
      BoxNode({
        style: styles.dark.ruleIndicatorsWrapper,
        key: `${key}-RuleIndicatorsWrapper`,
        children: headerRightSideChildren.map(child => componentMapping[child])
      })
    ],
    ...nonReadonlyPropsHeader,
    eventHandlers: {
      ...nonReadonlyPropsHeader.eventHandlers,
      onClick
    }
  });

  if (!disableExpandAndCollapse) {
    headerComponent.children.push(CollapseTriggerNode);
  }

  if (dropIndicatorIndex > -1) {
    children.splice(dropIndicatorIndex, 0, HorizontalDropIndicator());
  }

  let stepBodyStyle = style.body;
  if (!expanded) {
    stepBodyStyle = style.bodyHidden;
    // as of now, not using readonly style for flow view
  } else if (readonly && !isFlowMode) {
    stepBodyStyle = style.bodyReadonly;
  }

  const bodyComponent = BoxNode({
    key: `${key}-Body`,
    style: stepBodyStyle,
    children
  });

  const childNodes = [headerComponent, bodyComponent];
  const addChildNodes = [];

  if (addChildNode && expanded) {
    addChildNodes.push(addChildNode);
  }

  if (addChildNodes.length > 0) {
    const addChildNodesComponent = BoxNode({
      key: `${key}-AddChildNodes`,
      style: {
        justifyContent: Justify.FlexStart,
        flexDirection: FlexDirection.Row
      },
      variables: {
        swimlaneId
      },
      children: addChildNodes
    });

    childNodes.push(addChildNodesComponent);
  }
  return BoxNode({
    key,
    style: {
      ...(selected
        ? isFlowMode
          ? style.flowContainerSelected
          : style.containerSelected
        : isFlowMode
          ? style.flowContainer
          : style.container),
      ...styleOverride,
      ...(isDroppingAbove
        ? {
            marginTop:
              (style.container.marginTop ?? 0) -
              HORIZONTAL_DROP_INDICATOR_HEIGHT
          }
        : {})
    },
    variables: {
      swimlaneId
    },
    children: childNodes,
    ...nonReadonlyPropsContainer
  });
};

type GetTitlePrefixIconProps = {
  isSwimlaneFlow: boolean;
  isFlowMode: boolean;
  upStreamRulesCount: number;
};

const getTitlePrefixIcon = ({
  isSwimlaneFlow,
  isFlowMode,
  upStreamRulesCount
}: GetTitlePrefixIconProps): ComponentMappingKeys | null => {
  if (isFlowMode) {
    if (isSwimlaneFlow) {
      if (upStreamRulesCount > 0) {
        return "upStreamRuleIndicatorIcon";
      }
      return null;
    }
  }
  return "defaultTitlePrefixIcon";
};

type GetHeaderRightSideChildrenProps = {
  isSwimlaneFlow: boolean;
  isFlowMode: boolean;
  upStreamRulesCount: number;
  downStreamRulesCount: number;
};

const getHeaderRightSideChildren = ({
  isSwimlaneFlow,
  isFlowMode,
  upStreamRulesCount,
  downStreamRulesCount
}: GetHeaderRightSideChildrenProps): ComponentMappingKeys[] => {
  const children: ComponentMappingKeys[] = [];

  if (isFlowMode) {
    if (isSwimlaneFlow) {
      if (downStreamRulesCount > 0) {
        children.push("downStreamRuleIndicatorIcon");
      }
    }
  } else {
    if (upStreamRulesCount > 0) {
      children.push("upStreamRuleIndicatorIcon");
    }
    if (downStreamRulesCount > 0) {
      children.push("downStreamRuleIndicatorIcon");
    }
  }

  return children;
};
